import { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  ButtonBase,
  FormControlLabel,
  Grid,
  Pagination,
  Paper,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { BarChart, ChartsReferenceLine } from "@mui/x-charts";
import { ContentTitleBar } from "../../../components/ContentTitleBar";
import { useNavigate, useParams } from "react-router-dom";
import {
  acceptSync,
  rejectSync,
  getSyncResults,
  loadClip,
} from "../utils/dataLoader";
import { pluralizeNum } from "../utils/numHelpers";
import { WaitDialog } from "../../../components/dialogs";
import SyncChannelChart from "./SyncChannelChart";

// At or below this many channels every detail chart is shown at once;
// above it the charts are paged, PAGE_SIZE at a time.
const PAGING_THRESHOLD = 20;
const PAGE_SIZE = 12;
const DEFAULT_METRIC_RANGE = [-0.05, 1];

export default function SyncResultsPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { caseId, clipId } = useParams();
  const [loaded, setIsLoaded] = useState(false);
  const [clip, setClip] = useState(null);

  const handleAccept = async () => {
    setIsLoaded(false);
    await acceptSync(caseId, clipId);
    setIsLoaded(true);
    navigate(`/app/cases/${caseId}/clips/${clipId}`);
  };

  const handleRevert = async () => {
    setIsLoaded(false);
    await rejectSync(caseId, clipId);
    setIsLoaded(true);
    navigate(`/app/cases/${caseId}/clips/${clipId}`);
  };

  const [syncResults, setSyncResults] = useState(null);
  const [syncInput, setSyncInput] = useState(null);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [failedOnly, setFailedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const scrollRef = useRef(null);
  const headerRef = useRef(null);
  const detailsRef = useRef(null);

  const hasFailedSyncIndicator = syncResults?.channels.some(
    (item) => item.syncIndicator === false,
  );

  // Shared y-axis so curves are comparable across pages.
  const syncRange = syncResults?.metricRange
    ? [syncResults.metricRange.min, syncResults.metricRange.max]
    : DEFAULT_METRIC_RANGE;

  const allChannels = syncResults?.channels ?? [];
  const visibleChannels =
    hasFailedSyncIndicator && failedOnly
      ? allChannels.filter((channel) => !channel.syncIndicator)
      : allChannels;
  const isPaged = visibleChannels.length > PAGING_THRESHOLD;
  const pageCount = Math.max(1, Math.ceil(visibleChannels.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageStart = isPaged ? (currentPage - 1) * PAGE_SIZE : 0;
  const pageChannels = isPaged
    ? visibleChannels.slice(pageStart, pageStart + PAGE_SIZE)
    : visibleChannels;

  // Scroll so the details accordion sits just under the sticky header.
  const scrollToDetails = () => {
    const scroller = scrollRef.current;
    const details = detailsRef.current;
    if (!scroller || !details) return;
    const headerBottom =
      headerRef.current?.getBoundingClientRect().bottom ??
      scroller.getBoundingClientRect().top;
    scroller.scrollBy({
      top: details.getBoundingClientRect().top - headerBottom - 8,
      behavior: "smooth",
    });
  };

  const jumpToChannel = (channelNumber) => {
    let channels = visibleChannels;
    let index = channels.findIndex((c) => c.channelNumber === channelNumber);
    if (index === -1) {
      // Hidden by the failed-only filter; drop the filter to reveal it.
      setFailedOnly(false);
      channels = allChannels;
      index = channels.findIndex((c) => c.channelNumber === channelNumber);
    }
    if (index === -1) return;
    if (channels.length > PAGING_THRESHOLD) {
      setPage(Math.floor(index / PAGE_SIZE) + 1);
    }
    setDetailsExpanded(true);
    scrollToDetails();
  };

  const handleFailedOnlyChange = (event) => {
    setFailedOnly(event.target.checked);
    setPage(1);
  };

  const xAxisFormatter = (samples) => {
    let val = (samples / syncInput?.sampleRate) * 1000;
    let safeVal = isFinite(val) ? Math.trunc(val) : 0;
    return `${safeVal}`;
  };

  const tooltipFormatter = (samples) => {
    let val = (samples / syncInput?.sampleRate) * 1000;
    let safeVal = isFinite(val) ? Math.trunc(val) : 0;
    return `Offset: ${safeVal}ms (${samples} sample${pluralizeNum(samples)})`;
  };

  useEffect(() => {
    const fetchSyncResults = async () => {
      const currClip = await loadClip(caseId, clipId);
      setClip(currClip);

      const results = await getSyncResults(caseId, clipId);
      setSyncResults(results.syncResults);
      setSyncInput(results.inputConfig);
      setIsLoaded(true);
    };

    fetchSyncResults();
  }, [caseId, clipId]);

  return (
    <Stack
      component="main"
      height={1}
      // pb allows for scrolling
      pb={10}
      ref={scrollRef}
      sx={{ overflowY: "auto", overscrollBehavior: "contain" }}
    >
      <WaitDialog open={!loaded} />
      <Stack flex={1} p={2} pb={16} spacing={1}>
        {syncResults && (
          <>
            <Paper
              ref={headerRef}
              elevation={4}
              sx={{ p: 2, position: "sticky", top: 0, zIndex: 1 }}
            >
              <Typography textTransform="uppercase" variant="h6">
                Sync Results
              </Typography>
              <Typography textTransform="uppercase" variant="body1">
                Reference Channel:
                <Typography component="span" ml={0.5}>
                  {syncInput.refChannel + 1}
                </Typography>
              </Typography>
              {hasFailedSyncIndicator && (
                <Alert
                  severity="error"
                  sx={{ boxShadow: 1, mt: 1.5, width: "fit-content" }}
                >
                  Time offset align match not found for some channels. Please
                  review the results below.
                </Alert>
              )}
            </Paper>
            {hasFailedSyncIndicator ? (
              <Accordion
                defaultExpanded
                sx={{
                  "&.MuiAccordion-root:before": {
                    backgroundColor: "transparent",
                  },
                  borderRadius: 1,
                  boxShadow: 4,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <Typography
                    textTransform="uppercase"
                    fontWeight={500}
                    variant="body1"
                  >
                    Time Offset Align Match Results
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box display="flex" flexWrap="wrap" gap={1} maxWidth={700}>
                    {syncResults.channels.map((item) => (
                      <Stack alignItems="center" key={item.channelNumber}>
                        <Typography variant="caption">
                          CHAN {item.channelNumber + 1}
                        </Typography>
                        <ButtonBase
                          aria-label={`Show channel ${item.channelNumber + 1} details`}
                          onClick={() => jumpToChannel(item.channelNumber)}
                          sx={{
                            borderRadius: 2,
                            boxShadow: 3,
                            height: 60,
                            width: 60,
                            background: item.syncIndicator
                              ? `radial-gradient(at top left, ${theme.palette.success.light}, ${theme.palette.success.main})`
                              : `radial-gradient(at top left, ${theme.palette.error.light}, ${theme.palette.error.main})`,
                          }}
                        />
                      </Stack>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ) : (
              <Paper elevation={4} sx={{ p: 2 }}>
                <Typography
                  mb={2}
                  fontWeight={500}
                  textTransform="uppercase"
                  variant="body1"
                >
                  Adjustments
                </Typography>
                <BarChart
                  layout="horizontal"
                  borderRadius={10}
                  height={400}
                  grid={{ vertical: true }}
                  series={[
                    {
                      data: syncResults?.sampleAdjustments.map(
                        (adj) =>
                          adj -
                          syncResults.sampleAdjustments[syncInput.refChannel],
                      ),
                      valueFormatter: tooltipFormatter,
                    },
                  ]}
                  xAxis={[
                    {
                      valueFormatter: xAxisFormatter,
                      domainLimit: (min, max) => {
                        const absMax = Math.max(Math.abs(min), Math.abs(max));
                        return { min: -absMax, max: absMax };
                      },
                      label: "MILLISECONDS",
                      colorMap: {
                        type: "piecewise",
                        thresholds: [0],
                        colors: [
                          theme.palette.warning.light,
                          theme.palette.info.dark,
                        ],
                      },
                    },
                  ]}
                  yAxis={[
                    {
                      data: syncResults?.sampleAdjustments.map(
                        (_, index) => `${index + 1}`,
                      ),
                      label: "CHANNELS",
                      tickInterval: (value, index) => {
                        if (syncResults?.sampleAdjustments.length > 20) {
                          // Show fewer ticks for larger amount of channels
                          return index %
                            Math.ceil(
                              syncResults?.sampleAdjustments.length / 20,
                            ) ===
                            0
                            ? value
                            : null;
                        }
                        return value;
                      },
                      valueFormatter: (value, context) =>
                        context?.location === "tooltip"
                          ? `Channel ${value}`
                          : value,
                    },
                  ]}
                  margin={{ left: 0 }}
                  onItemClick={(_, { dataIndex }) => {
                    const channel = syncResults.channels[dataIndex];
                    if (channel) jumpToChannel(channel.channelNumber);
                  }}
                >
                  <ChartsReferenceLine
                    x={0}
                    lineStyle={{
                      shapeRendering: "crispEdges",
                      stroke: "rgba(0, 0, 0, .5)",
                      strokeWidth: 2.25,
                    }}
                  />
                </BarChart>
              </Paper>
            )}

            <Accordion
              ref={detailsRef}
              expanded={detailsExpanded}
              onChange={(_, expanded) => setDetailsExpanded(expanded)}
              // Without this the collapsed charts stay mounted.
              slotProps={{ transition: { unmountOnExit: true } }}
              sx={{
                "&.MuiAccordion-root:before": {
                  backgroundColor: "transparent",
                },
                borderRadius: 1,
                boxShadow: 4,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Typography
                  textTransform="uppercase"
                  fontWeight={500}
                  variant="body1"
                >
                  Detailed Results
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {(isPaged || hasFailedSyncIndicator) && (
                  <Stack
                    alignItems="center"
                    direction="row"
                    flexWrap="wrap"
                    gap={2}
                    mb={2}
                  >
                    {isPaged && (
                      <>
                        <Pagination
                          count={pageCount}
                          page={currentPage}
                          onChange={(_, value) => setPage(value)}
                          color="primary"
                        />
                        <Typography variant="body2" color="text.secondary">
                          Channels {pageStart + 1}–
                          {pageStart + pageChannels.length} of{" "}
                          {visibleChannels.length}
                        </Typography>
                      </>
                    )}
                    {hasFailedSyncIndicator && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={failedOnly}
                            onChange={handleFailedOnlyChange}
                          />
                        }
                        label="Failed channels only"
                        sx={{ ml: "auto" }}
                      />
                    )}
                  </Stack>
                )}
                <Grid container spacing={2}>
                  {pageChannels.map((channel) => (
                    <SyncChannelChart
                      key={channel.channelNumber}
                      caseId={caseId}
                      clipId={clipId}
                      channelNumber={channel.channelNumber}
                      isReference={
                        channel.channelNumber === syncInput.refChannel
                      }
                      showFailure={
                        hasFailedSyncIndicator && !channel.syncIndicator
                      }
                      yRange={syncRange}
                    />
                  ))}
                </Grid>
                {isPaged && (
                  <Stack alignItems="center" mt={2}>
                    <Pagination
                      count={pageCount}
                      page={currentPage}
                      onChange={(_, value) => {
                        setPage(value);
                        scrollToDetails();
                      }}
                      color="primary"
                    />
                  </Stack>
                )}
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Stack>
      <Paper
        elevation={4}
        sx={{
          display: "flex",
          alignItems: "center",
          p: 1,
          position: "fixed",
          transform: "translateX(-50%)",
          bottom: 64,
          mb: 1,
          left: "50%",
          zIndex: 1,
          width: "calc(100% - 1rem)",
        }}
      >
        <Button
          aria-label="Revert Changes"
          variant="outlined"
          sx={{ mr: 1 }}
          onClick={handleRevert}
        >
          Revert Changes
        </Button>
        <Button
          aria-label="Accept Results"
          disabled={syncResults && hasFailedSyncIndicator}
          loading={!syncResults}
          variant="contained"
          onClick={handleAccept}
        >
          Accept Results
        </Button>
      </Paper>
      <Box position="fixed" bottom={0} width={1} zIndex={1}>
        <ContentTitleBar
          name={clip?.description}
          label={clip?.afCase?.caseName}
          onBack={() => navigate(`/app/cases/${caseId}/clips`)}
        />
      </Box>
    </Stack>
  );
}
